import { useEffect, useState, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import CommandPalette from "./CommandPalette";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sparkles, Search, Bell, LogOut, Moon, Sun, User as UserIcon } from "lucide-react";
import { useAuth, ROLE_LABEL } from "@/lib/auth";
import { initials } from "@/lib/format";
import api from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import useNotificationsSocket from "@/lib/useNotificationsSocket";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const fetchUnread = () => {
    api.get("/notifications/unread-count").then(({ data }) => setUnread(data.count)).catch(() => {});
  };

  useEffect(() => {
    fetchUnread();
    // Slow safety-net poll only — realtime WS below handles the live path.
    const t = setInterval(fetchUnread, 300000);
    return () => clearInterval(t);
  }, [location.pathname]);

  // Realtime notifications via WebSocket
  const token = typeof window !== "undefined" ? localStorage.getItem("caos_access") : null;
  const handleWsMessage = useCallback((msg) => {
    if (msg.type === "notification" && msg.data) {
      setUnread((u) => u + 1);
      toast(msg.data.title || "New notification", {
        description: msg.data.body || undefined,
        action: msg.data.link
          ? { label: "Open", onClick: () => navigate(msg.data.link) }
          : undefined,
      });
      // Let any listening page (e.g. NotificationsPage) prepend the item.
      window.dispatchEvent(new CustomEvent("caos:notification", { detail: msg.data }));
    }
  }, [navigate]);
  useNotificationsSocket(user ? token : null, handleWsMessage);

  const title = titleFromPath(location.pathname);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} unread={unread} />
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 border-b border-border sticky top-0 bg-background/90 backdrop-blur-md z-30 flex items-center gap-3 px-6">
          <h1 className="font-heading text-base font-semibold tracking-tight" data-testid="page-title">{title}</h1>
          <div className="flex-1" />
          <button
            onClick={() => setCmdOpen(true)}
            data-testid="global-search-btn"
            className="hidden md:flex items-center gap-2 text-xs text-muted-foreground px-3 h-8 rounded-md border border-border hover:bg-muted transition-colors"
          >
            <Search size={13} /> Search or run command
            <span className="ml-4 font-mono text-[10px] bg-secondary rounded px-1.5 py-0.5 border border-border">⌘K</span>
          </button>
          <Button variant="ghost" size="icon" onClick={toggle} className="h-8 w-8" data-testid="theme-toggle">
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 relative"
            onClick={() => navigate("/notifications")}
            data-testid="header-notifications"
          >
            <Bell size={15} />
            {unread > 0 && (
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-brand-amber ring-2 ring-background" data-testid="unread-dot" />
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 h-8 pl-1 pr-2 rounded-md hover:bg-muted transition-colors" data-testid="user-menu">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-[10px] font-mono">{initials(user?.full_name)}</AvatarFallback>
                </Avatar>
                <span className="text-xs font-medium hidden sm:inline">{user?.full_name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-xs font-normal text-muted-foreground">{user?.email}</div>
                <div className="text-xs mt-0.5">{ROLE_LABEL[user?.role]}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="user-menu-settings">
                <UserIcon size={13} className="mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="user-menu-logout">
                <LogOut size={13} className="mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 px-6 py-6 slide-in">
          <Outlet context={{ openCmd: () => setCmdOpen(true), refreshUnread: fetchUnread }} />
        </main>
      </div>

      <CommandPalette
        open={cmdOpen}
        onOpenChange={setCmdOpen}
        onNewTask={() => navigate("/tasks?new=1")}
        onNewClient={() => navigate("/clients?new=1")}
      />
      <Toaster richColors position="top-right" />
    </div>
  );
}

function titleFromPath(p) {
  if (p === "/") return "Dashboard";
  if (p.startsWith("/clients")) return "Clients";
  if (p.startsWith("/tasks/recurring")) return "Recurring Tasks";
  if (p.startsWith("/tasks")) return "Tasks";
  if (p.startsWith("/invoices")) return "Invoices";
  if (p.startsWith("/documents")) return "Documents";
  if (p.startsWith("/notifications")) return "Notifications";
  if (p.startsWith("/activity")) return "Activity";
  if (p.startsWith("/users")) return "Team";
  if (p.startsWith("/settings")) return "Settings";
  return "CA OS";
}
